import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { GlobalContext } from "../../../context";
import { markAttendance } from "../../../context/actions/attendance";
import { fetchGamification, submitDailyAnswer } from "../../../context/actions/gamification";
import Icon from "../learn/Icon";
import { useLms } from "../learn/LmsShell";
import { dueLabel, fmtDay, fmtRange, fmtTime, greeting, isToday, useAt, useCountdown, WAT, zone } from "./time";

/*
 * Student Home, built with the approved prototype's markup and classes (styles in learn/lms.css).
 * Data: /me/overview (via LmsShell), /me/class, /me/gamification.
 */

// cohortId: the same course can be taken in two cohorts, so links say which one.
const courseUrl = (uuid, lessonId, tab, cohortId) => `/dashboard/course/${uuid}?${new URLSearchParams({ ...(lessonId ? { lesson: lessonId } : tab ? { tab } : {}), ...(cohortId ? { cohort_id: cohortId } : {}) })}`;
const THUMBS = ["ds", "ai", "da", "py"];
const thumbFor = (id) => THUMBS[(id || 0) % THUMBS.length];
const hrs = (a, b) => { const h = (new Date(b) - new Date(a)) / 3600000; return h >= 1 ? `${+h.toFixed(1)} ${h === 1 ? "hr" : "hrs"}` : `${Math.round(h * 60)} min`; };

/** The orange "Up next" card: the live/next class from /me/class, or the next lesson in the plan. */
function NextUp({ myClass, nextCourse, onAttended }) {
  const navigate = useNavigate();
  const [attended, setAttended] = useState(myClass?.attended ?? false);
  useEffect(() => setAttended(myClass?.attended ?? false), [myClass]);

  const lesson = nextCourse?.next_lesson;
  const start = myClass?.start_time ?? lesson?.start_time;
  const end = myClass?.end_time ?? (start && new Date(new Date(start).getTime() + 2 * 3600000).toISOString());
  const countdown = useCountdown(start);
  // Join opens 15 minutes before class: reload then so the button appears.
  useAt(myClass && !myClass.link ? myClass.join_opens_at : null, onAttended);
  if (!start) return null;

  const live = myClass ? myClass.status === "live" : lesson.state === "live";
  const today = isToday(start);
  const makeup = myClass?.type === "makeup";
  const title = myClass?.topic ?? lesson.title;
  const tag = [myClass?.course_name ?? nextCourse?.course.title, lesson && (!myClass || myClass.id === lesson.id) ? lesson.week_label : null].filter(Boolean).join(" · ");
  const details = nextCourse ? () => navigate(courseUrl(nextCourse.course.uuid, myClass?.id ?? lesson?.id, null, nextCourse.cohort?.id)) : null;

  const mark = async (silent) => {
    if (!myClass || attended || makeup) return;
    const res = await markAttendance(myClass.id);
    if (res.ok) { setAttended(true); onAttended?.(); toast.success("Attendance marked · lesson complete"); }
    else if (!silent) toast.error(res.message);
  };
  const join = () => { if (myClass?.link) window.open(myClass.link, "_blank", "noopener,noreferrer"); mark(true); };

  return (
    <section className="next">
      <div>
        <div className="label">{(live || today) && <span className="live-dot" />}{makeup ? "Makeup class" : live ? "Live now" : today ? "Live today" : `Next class · ${fmtDay(start)}`}</div>
        <h3>{title}</h3>
        <div className="meta"><span>{fmtRange(start, end)}</span><span>{hrs(start, end)}</span></div>
        {tag && <span className="ctag">{tag}</span>}
        {live && !makeup && myClass && <div className="count">{attended ? "✓ Attendance marked" : "Joining the class marks your attendance"}</div>}
        {!live && today && countdown && <div className="count">Starts in {countdown}</div>}
      </div>
      <div className="btns">
        {(live || today) && myClass?.link && <button className="btn primary" type="button" onClick={join}>Join class</button>}
        {(live || today) && myClass && !myClass.link && myClass.join_opens_at && <span className="btn ghost" aria-disabled="true" style={{ cursor: "default", opacity: 0.85 }}>Join opens {fmtTime(myClass.join_opens_at)}</span>}
        {details && <button className="btn ghost" type="button" onClick={details}>Class details</button>}
      </div>
    </section>
  );
}

/** In a cohort without a course yet (e.g. before tracks are assigned): reassure them they're in, and what to do meanwhile. */
export function CourseComing({ cohort }) {
  const starts = cohort.start_date ? new Date(`${cohort.start_date}T12:00:00Z`) : null;
  const upcoming = starts && starts > new Date();
  return (
    <section className="coming">
      <span className="coming-ico" aria-hidden="true">✓</span>
      <div>
        <strong>You're in the {cohort.name}{upcoming ? `, starting ${starts.toLocaleDateString("en-GB", { day: "numeric", month: "long", timeZone: "UTC" })}` : ""}</strong>
        <p>Your place is confirmed. Your course will appear here once it's assigned to you, and we'll email you when it does. There's nothing you need to do right now.</p>
        <p className="coming-next">While you wait, make sure your profile name is right: it's the name printed on your certificate.</p>
        <div className="coming-acts"><Link className="cbtn quiet" to="/dashboard/settings">Check your profile</Link></div>
      </div>
    </section>
  );
}

/** Today's challenge, prototype style. Same rules as before: answer until the reveal; 5 points if right. */
function Challenge({ question: initial, onPointsChanged }) {
  const [question, setQuestion] = useState(initial);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [left, setLeft] = useState(initial?.seconds_to_reveal ?? 0);

  useEffect(() => {
    setQuestion(initial);
    setLeft(initial?.seconds_to_reveal ?? 0);
    setResult(null);
    setSelected(initial?.answered ? initial.my_answer ?? null : null);
  }, [initial?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (left <= 0) return undefined;
    const t = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [left > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const revealed = left <= 0;
  const answered = question.answered || !!result;
  const canAnswer = !answered && !revealed;
  const pick = answered ? (result ? selected : question.my_answer) : selected;
  const pad = (n) => String(n).padStart(2, "0");
  const clock = `${pad(Math.floor(left / 3600))}:${pad(Math.floor((left % 3600) / 60))}:${pad(left % 60)}`;

  const submit = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    const res = await submitDailyAnswer(selected);
    setSubmitting(false);
    if (!res.ok) return toast.error(res.message);
    setResult(res.data);
    toast[res.data.correct ? "success" : "info"](res.data.correct ? "Correct! +5 points" : "Answer saved. See the reveal at 9:00 PM WAT.");
    const fresh = await fetchGamification();
    if (fresh) onPointsChanged?.(fresh);
  };

  return (
    <section className="card challenge">
      <div className="card-h"><h3>Today's challenge</h3><span className="pts">+5 PTS</span></div>
      {question.course_name && <span className="course-tag">{question.course_name}</span>}
      <p className="q">{question.question}</p>
      <div className="opts">
        {["a", "b", "c", "d"].map((o) => {
          const right = revealed && question.correct_option === o;
          const wrong = revealed && pick === o && question.correct_option && question.correct_option !== o;
          return (
            <button key={o} type="button" className={`opt ${right ? "right" : wrong ? "wrong" : ""}`} aria-pressed={!revealed && pick === o}
              disabled={!canAnswer} onClick={() => setSelected(o)}>
              {o.toUpperCase()}. {question[`option_${o}`]}
            </button>
          );
        })}
      </div>
      {revealed && question.explanation && <p className="feedback">{question.explanation}</p>}
      <div className="foot">
        {revealed ? <span className="reveal">Answer revealed</span> : <span className="reveal">Answer revealed in <b>{clock}</b></span>}
        {canAnswer ? (
          <button type="button" className="cbtn go" onClick={submit} disabled={!selected || submitting}>{submitting ? "Submitting…" : "Submit answer"}</button>
        ) : (
          <span>{result ? result.message : answered ? "You've answered. Come back for the reveal." : revealed ? "This question is closed." : ""}</span>
        )}
      </div>
    </section>
  );
}

export function CourseCard({ c }) {
  const navigate = useNavigate();
  const cls = c.status === "completed" ? "done" : c.status === "certificate_in_progress" ? "pending" : "";
  const badge = { active: ["active", "Active"], completed: ["done", "Completed"], certificate_in_progress: ["pending", "Certificate in progress"] }[c.status];
  const sub = c.status === "active"
    ? `${c.cohort.name}${c.week ? ` · ${c.week.label}${c.week.number ? ` of ${c.week.of}` : ""}` : ""}`
    : `${c.cohort.name}${c.ended_on ? ` · classes ended ${fmtDay(c.ended_on)}` : ""}`;

  return (
    <article className={`cc ${cls}`}>
      <div className={`thumb ${thumbFor(c.course.id)} ${c.course.image_url ? "has-img" : ""}`}>
        {c.course.image_url && <img src={c.course.image_url} alt="" />}
        <span>{c.course.title}</span>
      </div>
      <div className="cc-top"><h4>{c.course.title}</h4><span className={`badge ${badge[0]}`}>{badge[1]}</span></div>
      <span className="sub">{sub}</span>
      <div className="pbar"><i style={{ width: `${c.progress}%` }} /></div>
      <div className="pline"><span>Progress</span><b>{c.progress}%</b></div>
      {c.status === "active" && c.next_lesson && (
        <div className="nextup"><span>Continue</span>{c.next_lesson.week_label ? `${c.next_lesson.week_label} · ` : ""}{c.next_lesson.title}</div>
      )}
      {c.status === "certificate_in_progress" && (
        <div className="cert-note"><Icon name="clock" size={14} /><span>Classes have ended. Your certificate is issued once you meet the requirements and your capstone is marked.</span></div>
      )}
      <div className="act" style={c.status === "completed" ? { marginTop: "auto" } : undefined}>
        {c.status === "active" && <button className="cbtn go" type="button" onClick={() => navigate(courseUrl(c.course.uuid, c.next_lesson?.id, null, c.cohort?.id))}>Continue lesson</button>}
        {c.status === "completed" && <button className="cbtn go" type="button" onClick={() => navigate("/dashboard/certificates")}>View certificate</button>}
        <button className="cbtn quiet" type="button" onClick={() => navigate(courseUrl(c.course.uuid, null, null, c.cohort?.id))}>{c.status === "active" ? "View course" : "Review lessons"}</button>
      </div>
    </article>
  );
}

const DUE_ICON = { assignment: ["task", "task"], quiz: ["quiz", "assess"], project: ["live", "folder"] };

function DueThisWeek({ due, todayLessons }) {
  const navigate = useNavigate();
  const rows = [
    ...todayLessons.map((c) => ({
      key: `l${c.next_lesson.id}`, ico: ["live", "video"], title: c.next_lesson.title, course: c.course.title,
      meta: `live today, ${fmtTime(c.next_lesson.start_time)}`, st: ["today", "Today"], at: c.next_lesson.start_time, to: courseUrl(c.course.uuid, c.next_lesson.id, null, c.cohort?.id),
    })),
    ...due.map((d) => ({
      key: `${d.kind}${d.id}`, ico: DUE_ICON[d.kind], title: d.title, course: d.course.title,
      meta: d.kind, st: d.late ? ["due", "Late window"] : ["due", `Due ${dueLabel(d.due_at)}`], at: d.due_at,
      to: d.kind === "quiz" ? `/dashboard/course/${d.course.uuid}?quiz=${d.id}&cohort_id=${d.course.cohort_id}` : d.kind === "project" ? `/dashboard/course/${d.course.uuid}?project=${d.id}&cohort_id=${d.course.cohort_id}` : courseUrl(d.course.uuid, d.lesson_id, null, d.course.cohort_id),
    })),
  ].sort((a, b) => new Date(a.at) - new Date(b.at));

  return (
    <section className="card">
      <div className="card-h"><h3>Due this week</h3><span className="course-tag">Across your courses</span></div>
      {rows.length ? (
        <div className="items">
          {rows.map((r) => (
            <div key={r.key} className="item" role="button" tabIndex={0} style={{ cursor: "pointer" }}
              onClick={() => navigate(r.to)} onKeyDown={(e) => e.key === "Enter" && navigate(r.to)}>
              <span className={`ico ${r.ico[0]}`}><Icon name={r.ico[1]} /></span>
              <div><strong>{r.title}</strong><small><span className="course-tag">{r.course}</span> · {r.meta}</small></div>
              <span className={`st ${r.st[0]}`}>{r.st[1]}</span>
            </div>
          ))}
        </div>
      ) : <p className="course-tag">Nothing due in the next 7 days.</p>}
    </section>
  );
}

/** M–S strip: days this week covered by the current streak. */
function StreakStrip({ streak }) {
  const days = ["M", "T", "W", "T", "F", "S", "S"];
  // Streak days are counted on the server's WAT calendar, so the week is laid out in WAT too.
  const today = new Date(`${new Date().toLocaleDateString("en-CA", { timeZone: WAT })}T00:00:00Z`);
  const monday = new Date(today.getTime() - ((today.getUTCDay() + 6) % 7) * 86400000);
  const last = streak?.last_activity_date ? new Date(`${streak.last_activity_date}T00:00:00Z`) : null;
  const first = last && streak.current ? new Date(last.getTime() - (streak.current - 1) * 86400000) : null;
  return (
    <div className="streak" aria-label="Active days this week">
      {days.map((d, i) => {
        const day = new Date(monday.getTime() + i * 86400000);
        return <i key={i} className={first && day >= first && day <= last ? "on" : ""}>{d}</i>;
      })}
    </div>
  );
}

function ComingUp({ schedule }) {
  const items = (schedule || []).slice(0, 3);
  return (
    <section className="card">
      <div className="card-h"><h3>Coming up</h3></div>
      {items.length ? (
        <div className="sched">
          {items.map((s) => {
            const d = new Date(s.start_time);
            return (
              <div key={s.id} className="slot">
                <div className="date"><b>{d.toLocaleDateString("en-GB", { day: "2-digit", timeZone: zone() })}</b><span>{d.toLocaleDateString("en-GB", { weekday: "short", timeZone: zone() })}</span></div>
                <div><strong>{s.topic}</strong><small>{s.courses?.[0]?.title ? `${s.courses[0].title} · ` : ""}live, {fmtTime(s.start_time)}</small></div>
              </div>
            );
          })}
        </div>
      ) : <p className="course-tag">No classes scheduled yet.</p>}
    </section>
  );
}

export default function StudentHome({ myClass, onClassChange, gamification, gamificationLoading, onPointsChanged }) {
  const { authState: { data }, scheduleState } = useContext(GlobalContext);
  const { overview, overviewError } = useLms();

  const courses = overview?.courses ?? [];
  const active = courses.filter((c) => c.status === "active");
  const finished = courses.filter((c) => c.status !== "active");
  const hasActive = overview ? overview.has_active : true;
  // The daily challenge starts with full classes, not during orientation or before the cohort begins.
  const challengeOn = overview ? active.some((c) => c.teaching_started) : false;
  const question = gamification?.question ?? null;
  const nextCourse = (myClass && active.find((c) => c.course.title === myClass.course_name))
    || [...active].filter((c) => c.next_lesson).sort((a, b) => new Date(a.next_lesson.start_time) - new Date(b.next_lesson.start_time))[0];
  const todayLessons = active.filter((c) => c.next_lesson && dueLabel(c.next_lesson.start_time) === "Today");
  const entries = gamification?.leaderboard?.entries ?? [];
  const me = entries.find((e) => e.is_me);
  const dateLine = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: zone() });

  return (
    <div className="lms">
      <div className="main">
        <div className="top">
          <div>
            <h2>{greeting()}, {data?.user?.firstname}</h2>
            <p>{dateLine} · {hasActive
              ? `${active.length || "Your"} active ${active.length === 1 ? "course" : "courses"}${active[0] ? ` in the ${active[0].cohort.name}` : ""}.`
              : `Welcome back. You've finished ${finished.length} ${finished.length === 1 ? "course" : "courses"} with E1X.`}</p>
          </div>
          <div className="chips">
            {active[0] && <span className="chip brand">{active[0].cohort.name}</span>}
            {overview && !hasActive && <span className="chip">Alumni</span>}
          </div>
        </div>

        <div className="col">
          {hasActive && <NextUp myClass={myClass} nextCourse={nextCourse} onAttended={onClassChange} />}
          {overview && !hasActive && (
            <section className="welcome-back">
              <div>
                <h3>Ready for your next course?</h3>
                <p>You don't have a course running right now. Enrol in the next cohort to keep building, or revisit your past lessons below.</p>
              </div>
              <Link className="cbtn go" to="/courses/categories">Browse courses</Link>
            </section>
          )}

          {hasActive && challengeOn && (gamificationLoading ? <div className="skeleton" style={{ height: 220 }} />
            : question ? <Challenge question={question} onPointsChanged={onPointsChanged} />
            : (
              <section className="card challenge">
                <div className="card-h"><h3>Today's challenge</h3><span className="pts">+5 PTS</span></div>
                <p className="course-tag">No question today. Check back tomorrow.</p>
              </section>
            ))}

          <section className="sec" style={{ display: "grid", gap: 12 }}>
            <div className="sec-title"><h3>My courses</h3><Link to="/courses/categories">Browse courses →</Link></div>
            {overviewError && <p className="course-tag">We couldn't load your courses. Refresh the page to try again.</p>}
            {!overview && !overviewError && <div className="courses">{[1, 2].map((i) => <div key={i} className="skeleton" style={{ height: 300 }} />)}</div>}
            {overview?.waiting?.map((w) => <CourseComing key={w.id} cohort={w} />)}
            {overview && !courses.length && !overview.waiting?.length && <p className="course-tag">You're not enrolled in a course yet. <Link to="/courses/categories" style={{ color: "var(--brand)" }}>Browse courses</Link></p>}
            {courses.length > 0 && <div className="courses">{courses.map((c) => <CourseCard key={`${c.course.uuid}-${c.cohort.id}`} c={c} />)}</div>}
          </section>

          {hasActive && overview && <DueThisWeek due={overview.due} todayLessons={todayLessons} />}
        </div>

        <div className="col">
          {hasActive ? (
            <>
              <section className="card">
                <div className="stats">
                  <div className="stat"><b>{gamification?.points ?? 0}</b><span>Points</span></div>
                  <div className="stat"><b>{gamification?.streak?.current ?? 0}</b><span>Day streak</span></div>
                  <div className="stat"><b>{me ? `#${me.rank}` : "—"}</b><span>{gamification?.leaderboard?.course_id ? "Rank in your course" : "Rank in cohort"}</span></div>
                </div>
                <StreakStrip streak={gamification?.streak} />
                <p className="course-tag" style={{ marginTop: 10 }}>Keep your streak going each day: answer the daily challenge, attend a class, finish a recording, or submit a quiz, assignment or project.</p>
              </section>
              <section className="card">
                <div className="card-h"><h3>Leaderboard</h3>{active[0] && <Link to={courseUrl(active[0].course.uuid, null, "board", active[0].cohort?.id)} className="link-btn">Full board ›</Link>}</div>
                {gamificationLoading ? <div className="skeleton" style={{ height: 120 }} /> : entries.length ? (
                  <div className="board">
                    {entries.map((e, i) => e.gap
                      ? <div key={`gap${i}`} className="row"><span className="n">…</span><span className="who" /><span className="pts" /></div>
                      : <div key={`${e.rank}${e.name}`} className={`row ${e.is_me ? "me" : ""}`}><span className="n">{e.rank}</span><span className="who">{e.is_me ? "You" : e.name}</span><span className="pts">{e.points}</span></div>)}
                  </div>
                ) : <p className="course-tag">The leaderboard fills up as your cohort earns points.</p>}
                <small className="course-tag">Earn points by attending live classes (+10), passing quizzes (up to +50), submitting and passing projects, and answering the daily challenge correctly (+5). Only this cohort's points count.</small>
              </section>
              <ComingUp schedule={scheduleState?.data} />
            </>
          ) : (
            <>
              <section className="card">
                <div className="card-h"><h3>Your E1X journey</h3></div>
                <div className="stats">
                  <div className="stat"><b>{courses.length}</b><span>Courses taken</span></div>
                  <div className="stat"><b>{courses.filter((c) => c.status === "completed").length}</b><span>Certificates earned</span></div>
                  <div className="stat"><b>{courses.filter((c) => c.status === "certificate_in_progress").length}</b><span>Certificates in progress</span></div>
                </div>
              </section>
              <section className="card">
                <div className="card-h"><h3>Keep growing</h3></div>
                <div className="shortcuts">
                  {[["/dashboard/job-board", "brief", "Job board", "Roles matched to your courses"],
                    ["/dashboard/cv-review", "doc", "CV reviewer", "Get feedback on your CV"],
                    ["/dashboard/certificates", "award", "Certificates", "Download or share"]].map(([to, icon, t, s]) => (
                    <Link key={to} className="sc" to={to}><Icon name={icon} /><div><strong>{t}</strong><small>{s}</small></div></Link>
                  ))}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

